import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, MessageSquare, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import AppHeader from "@/components/AppHeader";
import AppFooter from "@/components/AppFooter";

// Simple client-side throttle: one submission per 30s per browser.
// Real abuse protection lives upstream (email queue + suppression list).
const RATE_LIMIT_MS = 30_000;
const RATE_LIMIT_KEY = "ezf:contact:lastSubmitAt";

const Contact = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Completează toate câmpurile.");
      return;
    }

    const last = Number(localStorage.getItem(RATE_LIMIT_KEY) ?? 0);
    if (Date.now() - last < RATE_LIMIT_MS) {
      const wait = Math.ceil((RATE_LIMIT_MS - (Date.now() - last)) / 1000);
      toast.error(`Așteaptă încă ${wait}s înainte să trimiți alt mesaj.`);
      return;
    }

    setSending(true);
    try {
      const cleanName = name.trim().slice(0, 100);
      const cleanEmail = email.trim().slice(0, 254);
      const cleanPhone = phone.trim().slice(0, 30) || null;
      const cleanMessage = message.trim().slice(0, 2000);
      const submissionId = crypto.randomUUID();

      const submittedAt = new Date().toLocaleString("ro-RO", {
        dateStyle: "long",
        timeStyle: "short",
        timeZone: "Europe/Bucharest",
      });

      // 1) Notification to inbox — primary delivery channel.
      const { error: notifyErr } = await supabase.functions.invoke(
        "send-transactional-email",
        {
          body: {
            templateName: "contact-form-notification",
            recipientEmail: "contact@ezfactura.ro",
            idempotencyKey: `contact-notify-${submissionId}`,
            templateData: {
              senderName: cleanName,
              senderEmail: cleanEmail,
              senderPhone: cleanPhone,
              message: cleanMessage,
              submittedAt,
            },
          },
        },
      );
      if (notifyErr) throw notifyErr;

      // 2) Confirmation to the sender (best-effort: failure here doesn't
      //    invalidate the user's submission — they already saw success once
      //    the inbox notification went through).
      const { error: confirmErr } = await supabase.functions.invoke(
        "send-transactional-email",
        {
          body: {
            templateName: "contact-form-confirmation",
            recipientEmail: cleanEmail,
            idempotencyKey: `contact-confirm-${submissionId}`,
            templateData: { name: cleanName, message: cleanMessage },
          },
        },
      );
      if (confirmErr) {
        console.warn("Contact confirmation send failed (non-blocking):", confirmErr);
      }

      localStorage.setItem(RATE_LIMIT_KEY, String(Date.now()));
      toast.success("Mesajul a fost trimis! Te vom contacta în curând.");
      setName("");
      setEmail("");
      setPhone("");
      setMessage("");
    } catch (err) {
      console.error("Contact form error:", err);
      toast.error(
        "A apărut o eroare. Încearcă din nou sau scrie-ne la contact@ezfactura.ro.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="container max-w-2xl py-16 px-4">
        <div className="text-center mb-12">
          <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="h-6 w-6 text-primary" />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-3">Contactează-ne</h1>
          <p className="text-muted-foreground max-w-md mx-auto">
            Ai o întrebare, o sugestie sau ai nevoie de ajutor? Scrie-ne și îți răspundem cât mai repede.
          </p>
        </div>

        <div className="rounded-xl border border-border bg-card p-6 sm:p-8 mb-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name">Nume</Label>
              <Input
                id="name"
                placeholder="Ion Popescu"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="ion@exemplu.ro"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="phone">
                Telefon <span className="text-muted-foreground font-normal">(opțional)</span>
              </Label>
              <Input
                id="phone"
                type="tel"
                placeholder="07XX XXX XXX"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="mt-1.5"
                maxLength={30}
              />
            </div>
            <div>
              <Label htmlFor="message">Mesaj</Label>
              <Textarea
                id="message"
                placeholder="Scrie mesajul tău aici..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                className="mt-1.5"
              />
            </div>
            <Button type="submit" className="w-full" size="lg" disabled={sending}>
              {sending ? "Se trimite..." : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Trimite Mesajul
                </>
              )}
            </Button>
          </form>
        </div>

        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Mail className="h-4 w-4 text-primary" />
            <span className="font-heading font-semibold text-sm">Email direct</span>
          </div>
          <a href="mailto:contact@ezfactura.ro" className="text-primary hover:underline text-sm">
            contact@ezfactura.ro
          </a>
        </div>
      </main>
      <AppFooter />
    </div>
  );
};

export default Contact;
